import "./index.css";
import React, { useEffect, useState, Fragment } from "react";
import { NavLink } from "react-router-dom";
import Firebase from "../../firebasehelper";
import { setFaviconAndTitle } from "./utils";
import {
  sendVerificationCodeToEmail,
  validateEmailCode,
} from "../../api/AuthApi";
import { checkIfEmailExists, createNewAdminAccount } from "./utils";
import { emailPattern, adminTypeMapping } from "./constants";
import ErrorMsg from "./components/ErrorMsg";
import YesNoModal from "./components/YesNoModal";
import AdminIcon from "../../assets/media/settings.png";
import NotFound from "./components/NotFound";
import BrandedBanner from "./components/BrandedBanner";
import { FadeLoader } from "react-spinners";
import EyeLineAdsIcon from "../../assets/media/AdminRegistration/eyelineads-icon-no-bg.png";

export default function AdminRegistration({ history }) {
  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);

  const pathSegment = searchParams.get("brand");
  const adminType = searchParams.get("type");

  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [brandData, setBrandData] = useState(null);
  const [inputType, setInputType] = useState("firstName");
  const [errorMsg, setErrorMsg] = useState("");
  const [modalContent, setModalContent] = useState({
    caption: "",
    content: "",
  });
  const [userInput, setUserInput] = useState({
    firstName: "",
    email: "",
    verificationCode: "",
    password: "",
    repeatPassword: "",
    brand: pathSegment,
  });

  useEffect(() => {
    const host = window.location.hostname.toLowerCase();
    const brandName = pathSegment.toLowerCase();
    if (
      !host.includes("localhost") &&
      !host.includes("ecosystem") &&
      !host.includes(brandName)
    ) {
      setNotFound(true);
      return;
    }

    const getBrandData = async () => {
      const isValidAdmin = adminTypeMapping[adminType];
      if (!isValidAdmin) {
        setNotFound(true);
        return;
      }

      try {
        const brandData = await Firebase.getBrandDataByPathSegment(pathSegment);
        if (brandData) {
          setBrandData(brandData);
          setFaviconAndTitle(`${brandData.name} - Portal`, brandData.icon);
        } else setNotFound(true);

        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.log("getBrandData error", e);
      }
    };

    getBrandData();
  }, []);

  const onInputChange = (inputValue) => {
    setUserInput({ ...userInput, [inputType]: inputValue });
  };

  const getSubmitBtnLabel = () => {
    switch (inputType) {
      case "firstName":
      case "phone":
      case "email":
      case "password":
      case "verificationCode":
        return "Continue";
      default:
        return "Submit";
    }
  };

  const goToPreviousQuestion = () => {
    // reset error messages
    setErrorMsg("");

    if (inputType === "repeatPassword") {
      setInputType("password");
      setUserInput({ ...userInput, repeatPassword: "" });
    } else if (inputType === "password") {
      setInputType("verificationCode");
      setUserInput({ ...userInput, password: "" });
    } else if (inputType === "verificationCode") {
      setInputType("email");
      setUserInput({ ...userInput, verificationCode: "" });
    } else if (inputType === "email") {
      setInputType("firstName");
      setUserInput({ ...userInput, phone: "", email: "" });
    }
  };

  const mapInputType = () => {
    switch (inputType) {
      case "password":
      case "repeatPassword":
        return "password";
      case "email":
        return "email";
      default:
        return "text";
    }
  };

  const getPlaceholder = () => {
    switch (inputType) {
      case "email":
        return "Enter your email address";
      case "verificationCode":
        return "Enter the 6-digit code sent to your email";
      case "password":
        return "Enter your password";
      case "repeatPassword":
        return "Confirm your password";
      default:
        return "Enter your first name";
    }
  };

  const renderBackButton = () => inputType !== "firstName";

  const sendCodeToEmail = async () => {
    setLoading(true);
    const { firstName, email } = userInput;

    try {
      // first check if email already exists
      const doesExistInAuth = await checkIfEmailExists(email);
      let adminData = await Firebase.getAgencyByEmail(email);

      if (adminData) {
        adminData = Object.values(adminData).find(
          (admin) => admin.brand === brandData.id
        );
      }

      const doesExistInCommunity = adminData ? true : false;

      // Case 1: The email is already registered with the community
      if (doesExistInCommunity) {
        setErrorMsg(
          `The email you entered is already registered with ${brandData.name}`
        );
        setLoading(false);
        return { nextInputType: null };
      }
      // Case 2: The email is already registered but with another community
      else if (doesExistInAuth && !doesExistInCommunity) {
        setErrorMsg(
          `Your email is in use with another brand. Register another.`
        );
        setLoading(false);
        return { nextInputType: null };
      }
      // Case 3: The email is not registered yet
      else if (!doesExistInAuth) {
        sendVerificationCodeToEmail(brandData.name, firstName, email);
      }
      setLoading(false);
      return { nextInputType: "verificationCode" };
    } catch (e) {
      setLoading(false);
      console.log("sendCodeToEmail() error", e);
      setErrorMsg("Sorry, something went wrong");
      return { nextInputType: null };
    }
  };

  const validateCode = async () => {
    setLoading(true);
    const { verificationCode, email } = userInput;
    try {
      const { data } = await validateEmailCode(verificationCode, email);
      const { isValidCode } = data;

      if (isValidCode) {
        setLoading(false);
        return { nextInputType: "password" };
      } else {
        setErrorMsg("The code you entered is incorrect");
        setLoading(false);
        return { nextInputType: null };
      }
    } catch (err) {
      console.log("validateCode() error", err);
      setErrorMsg("Sorry, something went wrong");
      setLoading(false);
      return { nextInputType: null };
    }
  };

  const registerNewAdminViaEmail = async () => {
    setLoading(true);
    const { firstName, email, password, repeatPassword } = userInput;
    try {
      const doPasswordsMatch = password === repeatPassword;
      if (doPasswordsMatch) {
        setUserInput({ ...userInput, repeatPassword: "" });

        // create account
        const mappedAdminType = adminTypeMapping[adminType];
        await createNewAdminAccount(
          email,
          password,
          firstName,
          brandData.id,
          mappedAdminType
        );

        setModalContent({
          caption: "Registration successful",
          content: `You're officially an admin of the ${brandData.name} community. Welcome aboard!`,
          yes: "Log in",
          onYes: onLoginButtonClick,
        });

        setTimeout(() => {
          setLoading(false);
          setOpen(true);
        }, 1000);

        return { nextInputType: null };
      } else if (!doPasswordsMatch) {
        setErrorMsg("The passwords you entered do not match");
        setUserInput({ ...userInput, repeatPassword: "" });
        setLoading(false);
        return { nextInputType: null };
      }
    } catch (err) {
      console.log("registerNewAdminViaEmail() error", err);
      setErrorMsg("Sorry, something went wrong");
      return { nextInputType: null };
    }
  };

  const onSubmit = async (e) => {
    e && e.preventDefault();

    setErrorMsg("");

    let res;
    if (inputType === "firstName") res = { nextInputType: "email" };
    else if (inputType === "email") res = await sendCodeToEmail();
    else if (inputType === "verificationCode") res = await validateCode();
    else if (inputType === "password")
      res = { nextInputType: "repeatPassword" };
    else if (inputType === "repeatPassword")
      res = await registerNewAdminViaEmail();

    res.nextInputType && setInputType(res.nextInputType);
  };

  const onLoginButtonClick = () => {
    history.push("/", {
      email: userInput.email,
    });
  };

  const isEyeLineAdsListerSignUp = () => {
    return (
      pathSegment.toLowerCase() === "eyelineads" &&
      adminType.toLowerCase() === "marketplacelister"
    );
  };

  const validateInput = () => {
    const input = userInput[inputType];
    if (inputType === "firstName") return input.length > 1;
    else if (inputType === "verificationCode") return input.length === 6;
    else if (inputType === "email") return emailPattern.test(input);
    else if (inputType === "password" || inputType === "repeatPassword")
      return input.length > 5;
  };

  const isValid = validateInput();

  return brandData || notFound ? (
    <main className="registration">
      {notFound ? (
        <NotFound />
      ) : (
        <Fragment>
          {isEyeLineAdsListerSignUp() && (
            <h2 className="registration__eyelineads-title registration--show-on-desktop">
              Make money listing your empty space{" "}
              <br className="registration--show-on-mobile" />
              today, making it{" "}
              <br className="registration--show-on-desktop-md" />
              available to brands{" "}
              <br className="registration--show-on-mobile" />
              seeking to get into Melbourne&apos;s eyeline.
            </h2>
          )}
          <div
            className={`registration__wrapper ${
              isEyeLineAdsListerSignUp() ? "registration__branded-wrapper" : ""
            }`}
          >
            <section className="registration__signup-section">
              <img
                src={brandData.logo}
                alt="brand logo"
                className="registration__brand-logo"
              />
              {isEyeLineAdsListerSignUp() && (
                <h2 className="registration__eyelineads-title registration--show-on-mobile">
                  Make money listing your empty space{" "}
                  <br className="registration--show-on-mobile" />
                  today, making it{" "}
                  <br className="registration--show-on-desktop" />
                  available to brands{" "}
                  <br className="registration--show-on-mobile" />
                  seeking to get into Melbourne&apos;s eyeline.
                </h2>
              )}
              <h1 className="registration__header">{`Sign up as a ${adminTypeMapping[adminType]}`}</h1>
              <form className="registration__form">
                <div className="registration__input-container">
                  <input
                    autoFocus
                    type={mapInputType()}
                    className="registration__form-input"
                    id={inputType}
                    name={inputType}
                    placeholder={getPlaceholder()}
                    onChange={(e) => onInputChange(e.target.value)}
                    value={userInput[inputType]}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") onSubmit(e);
                    }}
                  />
                  {isLoading ? (
                    <div className="registration__loader">
                      <i className="fa fa-1x fa-sync fa-spin text-muted" />
                    </div>
                  ) : null}
                  {errorMsg ? <ErrorMsg msg={errorMsg} /> : null}
                </div>
                <div className="registration__btn-container">
                  <button
                    type="button"
                    className="btn btn-block btn-hero-lg btn-hero-primary"
                    onClick={onSubmit}
                    disabled={!isValid || isLoading}
                  >
                    {getSubmitBtnLabel()}
                  </button>
                  {renderBackButton() && (
                    <button
                      type="button"
                      className="btn btn-block btn-hero-lg btn-hero-success registration__back-btn"
                      onClick={goToPreviousQuestion}
                      disabled={isLoading}
                    >
                      Back
                    </button>
                  )}
                  <NavLink
                    to="/"
                    className="btn-register btn-register--margin-top"
                  >
                    Already have an account?
                  </NavLink>
                </div>
              </form>
            </section>
            <section
              className={
                isEyeLineAdsListerSignUp()
                  ? "registration__branded-hero"
                  : "registration__hero"
              }
            >
              {isEyeLineAdsListerSignUp() ? (
                <BrandedBanner />
              ) : (
                <div className="registration__banner" />
              )}
            </section>
          </div>
        </Fragment>
      )}

      <YesNoModal
        visible={isOpen}
        caption={modalContent.caption}
        content={modalContent.content}
        icon={AdminIcon}
        yes={modalContent.yes}
        onYes={modalContent.onYes}
      />
    </main>
  ) : (
    <main className="registration__fade-loader">
      {pathSegment === "eyelineads" ? (
        <img
          src={EyeLineAdsIcon}
          alt="eyelineads icon"
          className="registration__loading-brand-icon"
        />
      ) : (
        <div className="registration__loading-brand-icon" />
      )}
      <FadeLoader sizeUnit={"px"} size={100} loading={true} />
    </main>
  );
}



// WEBPACK FOOTER //
// ./src/screens/AdminRegistration/index.js